import { Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TabsContent } from '@/components/ui/tabs'
import { ToggleTabs } from '@/components/ui/ToggleTabs'
import { ChatActionWithValue } from '../../models/insuranceApplication'
import { TypingDots } from './TypingIndicator'

interface LossRunEntry {
  insurer: string
  periodStart: string
  periodEnd: string
  claimCount: string
  totalLosses: string
  reserve: string
  incidentDate: string
}

type CoverageTab = 'auto_liability' | 'physical_damage' | 'cargo' | 'general_liability'

const COVERAGE_TABS: { key: CoverageTab; label: string }[] = [
  { key: 'auto_liability', label: 'Auto Liability' },
  { key: 'physical_damage', label: 'Physical Damage' },
  { key: 'cargo', label: 'Cargo' },
  { key: 'general_liability', label: 'General Liability' },
]

function emptyEntry(): LossRunEntry {
  return {
    insurer: '',
    periodStart: '',
    periodEnd: '',
    claimCount: '',
    totalLosses: '',
    reserve: '',
    incidentDate: '',
  }
}

interface ChatLossRunFormProps {
  handleSubmit: (data: ChatActionWithValue | FormData) => void
  readonly?: boolean
  defaultValues?: Record<CoverageTab, LossRunEntry[]>
  isParsing?: boolean
}

function normalizeDefaultValues(
  defaultValues?: Record<CoverageTab, LossRunEntry[]>
): Record<CoverageTab, LossRunEntry[]> {
  const dv = defaultValues as any
  return {
    auto_liability: dv?.autoLiability ?? dv?.auto_liability ?? [emptyEntry()],
    physical_damage: dv?.physicalDamage ?? dv?.physical_damage ?? [emptyEntry()],
    cargo: dv?.cargo ?? [emptyEntry()],
    general_liability: dv?.generalLiability ?? dv?.general_liability ?? [emptyEntry()],
  }
}

export function ChatLossRunForm({ handleSubmit, readonly, defaultValues, isParsing }: ChatLossRunFormProps) {
  const [forceShowForm, setForceShowForm] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [entries, setEntries] = useState<Record<CoverageTab, LossRunEntry[]>>(() =>
    normalizeDefaultValues(defaultValues)
  )

  // Guard by serialized value so reference churn (parent re-render, same data) doesn't wipe edits.
  const prevDefaultValuesRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const serialized = JSON.stringify(defaultValues)
    if (serialized !== prevDefaultValuesRef.current) {
      prevDefaultValuesRef.current = serialized
      setEntries(normalizeDefaultValues(defaultValues))
    }
  }, [defaultValues])

  function updateEntry(tab: CoverageTab, index: number, field: keyof LossRunEntry, value: string) {
    setEntries((prev) => {
      const updated = [...prev[tab]]
      updated[index] = { ...updated[index], [field]: value }
      return { ...prev, [tab]: updated }
    })
  }

  function addEntry(tab: CoverageTab) {
    setEntries((prev) => ({ ...prev, [tab]: [...prev[tab], emptyEntry()] }))
  }

  function removeEntry(tab: CoverageTab, index: number) {
    setEntries((prev) => {
      const updated = prev[tab].filter((_, i) => i !== index)
      return { ...prev, [tab]: updated.length > 0 ? updated : [emptyEntry()] }
    })
  }

  const hasData = COVERAGE_TABS.some(({ key }) =>
    entries[key].some((e) => e.insurer || e.claimCount || e.totalLosses)
  )

  function onSubmit() {
    const summaryParts = COVERAGE_TABS.map(({ key, label }) => {
      const filled = entries[key].filter((e) => e.insurer || e.claimCount)
      return `${label}: ${filled.length} period(s)`
    })

    handleSubmit({
      content: {
        component: 'P',
        content: `Loss run history submitted — ${summaryParts.join(', ')}`,
      },
      value: entries,
    })
  }

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files
    if (!files || files.length === 0) return
    const formData = new FormData()
    Array.from(files).forEach((file) => formData.append('file', file))
    setForceShowForm(false)
    handleSubmit(formData)
    e.target.value = ''
  }

  if (isParsing && !forceShowForm) {
    return (
      <div className="w-full space-y-4">
        <TypingDots />
        <p className="text-muted-foreground text-sm">
          Updating your application with your loss run reports — this may take a few minutes. A form with the
          details will be populated momentarily for confirmation.
        </p>
        <div className="flex flex-col items-end justify-end gap-2">
          <Button variant="outline" onClick={() => setForceShowForm(true)}>
            Fill in manually
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              handleSubmit({
                content: { component: 'P', content: 'Skipping loss run reports' },
                value: 'SKIP_LOSS_RUNS',
              })
            }
          >
            Skip loss runs
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      <ToggleTabs
        defaultValue="auto_liability"
        className="w-full"
        items={COVERAGE_TABS.map(({ key, label }) => ({ value: key, label }))}
      >
        {COVERAGE_TABS.map(({ key, label }) => (
          <TabsContent key={key} value={key} className="space-y-4 pt-2">
            {entries[key].map((entry, index) => (
              <div key={index} className="space-y-3 rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <span className="text-foreground text-sm font-medium">
                    {label} — Period {index + 1}
                  </span>
                  {entries[key].length > 1 && !readonly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeEntry(key, index)}
                      className="text-destructive h-7 w-7"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Insurance Carrier</Label>
                    <Input
                      placeholder="e.g. Progressive Commercial"
                      value={entry.insurer}
                      onChange={(e) => updateEntry(key, index, 'insurer', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Policy Start Date</Label>
                    <Input
                      type="date"
                      value={entry.periodStart}
                      onChange={(e) => updateEntry(key, index, 'periodStart', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Policy End Date</Label>
                    <Input
                      type="date"
                      value={entry.periodEnd}
                      onChange={(e) => updateEntry(key, index, 'periodEnd', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Number of Claims</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={entry.claimCount}
                      onChange={(e) => updateEntry(key, index, 'claimCount', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Total Losses Paid ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={entry.totalLosses}
                      onChange={(e) => updateEntry(key, index, 'totalLosses', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Total Reserves ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={entry.reserve}
                      onChange={(e) => updateEntry(key, index, 'reserve', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Loss</Label>
                    <Input
                      type="date"
                      value={entry.incidentDate}
                      onChange={(e) => updateEntry(key, index, 'incidentDate', e.target.value)}
                      disabled={readonly}
                    />
                  </div>
                </div>
              </div>
            ))}

            {!readonly && (
              <Button variant="outline" size="sm" onClick={() => addEntry(key)} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Add another period
              </Button>
            )}
          </TabsContent>
        ))}
      </ToggleTabs>

      {!readonly && (
        <div className="flex flex-col items-end justify-end gap-2">
          <p className="text-muted-foreground text-sm">
            Uploading your official report gives you full credit with every carrier — manually entered totals
            may not count for carriers that require a verifiable, recent report.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.pdf,.jpg,.jpeg,.png,.xlsx,.xls"
            className="hidden"
            onChange={onFileChange}
          />
          <Button onClick={() => fileInputRef.current?.click()}>
            {hasData ? 'Upload again' : 'Upload document'}
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              handleSubmit({
                content: { component: 'P', content: 'Skipping loss run reports' },
                value: 'SKIP_LOSS_RUNS',
              })
            }
          >
            Skip loss runs
          </Button>
          <Button variant="outline" onClick={onSubmit}>
            Submit loss run history
          </Button>
        </div>
      )}
    </div>
  )
}
